/**
 * <noia-typewriter> starts empty, then types, holds, deletes and cycles through a list of words.
 *
 * Expected DOM (rendered by templates/index.liquid):
 *   <noia-typewriter data-words="WORKS,MAGAZINE,…">
 *     <span data-typewriter-text></span>
 *   </noia-typewriter>
 *
 * The animated text is aria-hidden by the template, which supplies an accessible label.
 * With reduced motion, the first word is shown statically.
 */
const TYPE_DELAY = 170; // per letter while typing
const TYPE_JITTER = 60; // random extra per letter, for a hand-typed rhythm
const DELETE_DELAY = 55; // per letter while deleting
const HOLD_DELAY = 2000; // full word on screen
const GAP_DELAY = 120; // empty pause between words
const START_DELAY = 250; // "NOIA." alone before the first word

class NoiaTypewriter extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector("[data-typewriter-text]");
    this.words = (this.dataset.words || "")
      .split(",")
      .map((word) => word.trim())
      .filter(Boolean);

    if (!this.textEl || this.words.length === 0) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.textEl.textContent = this.words[0];
      return;
    }

    this.wordIndex = 0;
    this.charCount = 0;
    this.deleting = false;
    this.timer = window.setTimeout(() => this.tick(), START_DELAY);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }

  tick() {
    const word = this.words[this.wordIndex];
    let delay;

    if (this.deleting) {
      this.charCount -= 1;
      delay = DELETE_DELAY;
      if (this.charCount <= 0) {
        this.charCount = 0;
        this.deleting = false;
        this.wordIndex = (this.wordIndex + 1) % this.words.length;
        delay = GAP_DELAY;
      }
    } else {
      this.charCount += 1;
      delay = TYPE_DELAY + Math.random() * TYPE_JITTER;
      if (this.charCount >= word.length) {
        this.charCount = word.length;
        this.deleting = true;
        delay = HOLD_DELAY;
      }
    }

    this.textEl.textContent = this.words[this.wordIndex].slice(0, this.charCount);
    this.timer = window.setTimeout(() => this.tick(), delay);
  }
}

if (!customElements.get("noia-typewriter")) {
  customElements.define("noia-typewriter", NoiaTypewriter);
}
